"use client";

/**
 * Prefer-over TagField — multi-select chips + creatable combobox.
 * Behavior SoT: shadcn-space combobox-08 (multi) ∪ combobox-10 (creatable).
 * Installed via composition on Command/Popover (registry install needs license env).
 */

import { CheckIcon, ChevronsUpDownIcon, PlusIcon, XIcon } from "lucide-react";
import { useId, useMemo, useState, type ReactNode } from "react";
import { cn } from "../../lib/cn";
import { Button } from "../button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "../command";
import { Popover, PopoverContent, PopoverTrigger } from "../popover";
import { buildControlDescribedBy } from "./field-a11y";
import { FieldShell, type FieldLayoutHint } from "./field-shell";

export interface TagFieldOption {
	id: string;
	label: string;
}

export interface TagFieldProps {
	id: string;
	label: string;
	/** Selected tag ids (stable). */
	value: string[];
	options: readonly TagFieldOption[];
	onChange: (value: string[]) => void;
	/**
	 * Creatable path — called when user commits an unknown label.
	 * Must return the new option (id+label); parent typically calls library.ensureTag.
	 */
	onCreateTag?: (label: string) => Promise<TagFieldOption> | TagFieldOption;
	description?: string;
	error?: ReactNode;
	reserveErrorSpace?: boolean;
	layoutHint?: FieldLayoutHint;
	className?: string;
	disabled?: boolean;
	placeholder?: string;
	searchPlaceholder?: string;
	createLabel?: (query: string) => string;
	emptyLabel?: string;
}

export function TagField({
	id,
	label,
	value,
	options,
	onChange,
	onCreateTag,
	description,
	error,
	reserveErrorSpace = false,
	layoutHint,
	className,
	disabled = false,
	placeholder = "Add tags…",
	searchPlaceholder = "Search or create…",
	createLabel = (q) => `Create “${q}”`,
	emptyLabel = "No tags found.",
}: TagFieldProps) {
	const listId = useId();
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const [creating, setCreating] = useState(false);

	const selected = useMemo(() => {
		const byId = new Map(options.map((o) => [o.id, o]));
		return value
			.map((idValue) => byId.get(idValue))
			.filter((o): o is TagFieldOption => Boolean(o));
	}, [options, value]);

	const controlDescribedBy = buildControlDescribedBy({
		id,
		error,
		hasDescription: Boolean(description),
	});

	const normalizedQuery = query.trim();
	const queryLower = normalizedQuery.toLowerCase();
	const filtered = options.filter((o) =>
		o.label.toLowerCase().includes(queryLower),
	);
	const exactMatch = options.some(
		(o) => o.label.toLowerCase() === queryLower,
	);
	const canCreate =
		Boolean(onCreateTag) &&
		normalizedQuery.length > 0 &&
		!exactMatch &&
		!creating;

	function toggle(tagId: string) {
		if (value.includes(tagId)) {
			onChange(value.filter((v) => v !== tagId));
			return;
		}
		onChange([...value, tagId]);
	}

	function remove(tagId: string) {
		onChange(value.filter((v) => v !== tagId));
	}

	async function handleCreate() {
		if (!onCreateTag || !normalizedQuery || creating) {
			return;
		}
		setCreating(true);
		try {
			const created = await onCreateTag(normalizedQuery);
			if (!value.includes(created.id)) {
				onChange([...value, created.id]);
			}
			setQuery("");
			setOpen(false);
		} finally {
			setCreating(false);
		}
	}

	return (
		<FieldShell
			id={id}
			label={label}
			description={description}
			error={error}
			reserveErrorSpace={reserveErrorSpace}
			layoutHint={layoutHint}
			className={className}
		>
			<div
				className="flex flex-col gap-2"
				data-testid={`${id}-tag-field`}
				data-sq-tag-field=""
			>
				{selected.length > 0 ? (
					<ul
						id={listId}
						className="flex flex-wrap gap-1.5"
						aria-label={label}
					>
						{selected.map((tag) => (
							<li key={tag.id}>
								<span
									className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/40 px-2 py-0.5 text-xs"
									data-testid={`${id}-chip-${tag.id}`}
								>
									{tag.label}
									<button
										type="button"
										className="rounded-sm p-0.5 hover:bg-muted"
										aria-label={`Remove ${tag.label}`}
										disabled={disabled}
										onClick={() => remove(tag.id)}
									>
										<XIcon className="size-3" />
									</button>
								</span>
							</li>
						))}
					</ul>
				) : null}

				<Popover open={open} onOpenChange={setOpen}>
					<PopoverTrigger asChild>
						<Button
							id={id}
							type="button"
							variant="outline"
							role="combobox"
							aria-expanded={open}
							aria-controls={listId}
							aria-describedby={controlDescribedBy}
							disabled={disabled}
							className={cn(
								"w-full justify-between font-normal",
								selected.length === 0 && "text-muted-foreground",
							)}
							data-testid={`${id}-trigger`}
						>
							{placeholder}
							<ChevronsUpDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
						</Button>
					</PopoverTrigger>
					<PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
						<Command shouldFilter={false}>
							<CommandInput
								placeholder={searchPlaceholder}
								value={query}
								onValueChange={setQuery}
								disabled={disabled || creating}
							/>
							<CommandList>
								<CommandEmpty>{emptyLabel}</CommandEmpty>
								<CommandGroup>
									{filtered.map((option) => {
										const checked = value.includes(option.id);
										return (
											<CommandItem
												key={option.id}
												value={`${option.label} ${option.id}`}
												onSelect={() => {
													toggle(option.id);
												}}
												data-testid={`${id}-option-${option.id}`}
											>
												<CheckIcon
													className={cn(
														"mr-2 size-4",
														checked
															? "opacity-100"
															: "opacity-0",
													)}
												/>
												{option.label}
											</CommandItem>
										);
									})}
									{canCreate ? (
										<CommandItem
											value={`__create__${normalizedQuery}`}
											onSelect={() => {
												void handleCreate();
											}}
											data-testid={`${id}-create`}
										>
											<PlusIcon className="mr-2 size-4" />
											{createLabel(normalizedQuery)}
										</CommandItem>
									) : null}
								</CommandGroup>
							</CommandList>
						</Command>
					</PopoverContent>
				</Popover>
			</div>
		</FieldShell>
	);
}
